import React, { useCallback, useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import StatCard from '../components/StatCard';
import Card, { CardHeader } from '../components/Card';
import DataTable, { Column } from '../components/DataTable';
import Badge, { BadgeTone } from '../components/Badge';
import Button from '../components/Button';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import Pagination from '../components/Pagination';
import SearchInput from '../components/SearchInput';
import Toast, { ToastMessage } from '../components/Toast';
import axios from 'axios';
import { BaseUrl } from '../services/adminApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faShoppingCart,
  faCircleCheck,
  faHourglassHalf,
  faCircleXmark,
  faBan,
  faCopy,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';

type OrderStatus = 'created' | 'attempted' | 'paid' | 'failed' | 'cancelled';

interface CustomerOption {
  id: string;
  full_name: string;
  email: string;
  role: string;
  organisation: string;
}
interface RawOrder {
  order_id: number;
  order_code: string;
  customer_name: string;
  customer_email: string;
  plan_name: string;
  billing_cycle: string;
  payment_method: string | null;
  amount: number | string;
  currency: string;
  status: string;
  created_at: string;
}

interface OrderRow {
  id: number;        
  order_code: string; 
  customer_name: string;
  customer_email: string;
  plan_name: string;
  billing_cycle: string;
  payment_method: string | null;
  amount: number;
  currency: string; 
  status: OrderStatus;
  created_at: string;
}

function mapOrder(raw: RawOrder): OrderRow {
  return {
    id: raw.order_id,
    order_code: raw.order_code,
    customer_name: raw.customer_name,
    customer_email: raw.customer_email,
    plan_name: raw.plan_name,
    billing_cycle: raw.billing_cycle,
    payment_method: raw.payment_method,
    amount: Number(raw.amount) || 0,
    currency: raw.currency,
    status: (raw.status || '').toLowerCase() as OrderStatus,
    created_at: raw.created_at,
  };
}

const PAGE_SIZE = 10;
const STATUS_TABS: (OrderStatus | 'all')[] = ['all', 'created', 'attempted', 'paid', 'failed', 'cancelled'];

const STATUS_TONE: Record<OrderStatus, BadgeTone> = {
  paid: 'green',
  created: 'blue',
  attempted: 'amber',
  failed: 'red',
  cancelled: 'red',
};

const CURRENCY_SYMBOLS: Record<string, string> = { INR: '₹', USD: '$', EUR: '€', GBP: '£' };

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatAmount(row: OrderRow) {
  const symbol = CURRENCY_SYMBOLS[row.currency] ?? `${row.currency} `;
  const decimals = Number.isInteger(row.amount) ? 0 : 2;
  return `${symbol}${row.amount.toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
}


function makeLocalOrderCode() {
  return `ORDER-LOCAL-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
}

const ACCOUNT_TYPE_OPTIONS = ['individual', 'institution', 'company', 'demo'] as const;
const ACCOUNT_TYPE_LABELS: Record<(typeof ACCOUNT_TYPE_OPTIONS)[number], string> = {
  individual: 'Individual',
  institution: 'Institution',
  company: 'Publisher',
  demo: 'Demo',
};
const ACCOUNT_TYPE_BILLING_CYCLE: Record<(typeof ACCOUNT_TYPE_OPTIONS)[number], string> = {
  individual: 'Annual',
  institution: 'Annual',
  company: 'Annual',
  demo: 'Monthly',
};

const DEFAULT_CURRENCY = 'INR';

// Shape returned by GET /plans/?type=&billing_cycle=&currency=
interface PlanPricing {
  pricing_id: number;
  currency_id: number;
  billing_cycle: string;
  price: number;
  discount_percentage: number;
  discounted_amount: number;
  trial_days: number;
  razorpay_plan_id: string | null;
}

interface PlanOption {
  plan_id: number;
  name: string;
  plan_type: string;
  description: string;
  billing_cycle: string;
  pricing_id: number;
  price: number;
  discounted_amount: number;
  gst_amount: number;
  total_amount: number;
  currency_code: string;
  currency_symbol: string;
  pricing: PlanPricing[];
}

interface PlansResponse {
  currency: { currency_id: number; code: string; symbol: string; show_gst: boolean; gst_rate: number };
  billing_cycle: string;
  plans: PlanOption[];
}

const PAYMENT_METHOD_OPTIONS = ['razorpay', 'UPI', 'Card', 'Netbanking', 'Wallet'] as const;

const CREATE_STATUS_OPTIONS: OrderStatus[] = ['created', 'paid'];

interface NewOrderForm {
  customer_name: string;
  customer_email: string;
  account_type: (typeof ACCOUNT_TYPE_OPTIONS)[number];
  pricing_id: number;
  amount: string;
  payment_method: (typeof PAYMENT_METHOD_OPTIONS)[number];
  status: OrderStatus;
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

const EMPTY_FORM: NewOrderForm = {
  customer_name: '',
  customer_email: '',
  account_type: 'individual',
  pricing_id: 0, // populated once GET /plans/ resolves for the selected account type
  amount: '',
  payment_method: PAYMENT_METHOD_OPTIONS[0],
  status: 'created',
  razorpay_payment_id: '',
  razorpay_order_id: '',
  razorpay_signature: '',
};

interface OrderMetrics {
  total_orders: number;
  paid: number;
  created_attempted: number;
  failed_cancelled: number;
}

const EMPTY_METRICS: OrderMetrics = { total_orders: 0, paid: 0, created_attempted: 0, failed_cancelled: 0 };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function OrderManagement() {
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_TABS)[number]>('all');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selected, setSelected] = useState<OrderRow | null>(null);
  const [cancelTarget, setCancelTarget] = useState<OrderRow | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [metrics, setMetrics] = useState<OrderMetrics>(EMPTY_METRICS);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState<CustomerOption[]>([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [showCustomerResults, setShowCustomerResults] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | null>(null);

  // --- Create order state ---
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<NewOrderForm>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [planOptions, setPlanOptions] = useState<PlanOption[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);

  const showToast = (text: string, type: ToastMessage['type'] = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3000);
  };
  // GET /admin/users/?pagenum=&pagesize=&search=
  useEffect(() => {
    if (!showCreate || !customerSearch.trim()) {
      setCustomerResults([]);
      return;
    }
    setCustomerSearchLoading(true);
    const handle = setTimeout(async () => {
      try {
        const res = await axios.get(BaseUrl + 'admin/users/', {
          params: { pagenum: 1, pagesize: 10, search: customerSearch.trim() },
        });
        setCustomerResults(res.data?.results ?? []);
        setShowCustomerResults(true);
      } catch {
        setCustomerResults([]);
      } finally {
        setCustomerSearchLoading(false);
      }
    }, 350); // debounce so we don't hit the API on every keystroke

    return () => clearTimeout(handle);
  }, [customerSearch, showCreate]);

  // GET /plans/?type=&billing_cycle=&currency= — reloads whenever the
  // create-order modal is open and the selected account type changes, so
  // the Plan dropdown always reflects real, currently-sellable plans
  // (with their real pricing_id) instead of a hardcoded guess.
  useEffect(() => {
    if (!showCreate) return;
    let cancelled = false;
    setPlansLoading(true);
    const billingCycle = ACCOUNT_TYPE_BILLING_CYCLE[form.account_type];
    axios
      .get<PlansResponse>(BaseUrl + 'plans/', {
        params: { type: form.account_type, billing_cycle: billingCycle, currency: DEFAULT_CURRENCY },
      })
      .then(res => {
        if (cancelled) return;
        const plans = (res.data?.plans ?? []).filter(p =>
          form.account_type === 'demo' ? true : (p.plan_type?.toLowerCase() !== 'free' && p.price > 0)
        );
        setPlanOptions(plans);
        setForm(prev => ({ ...prev, pricing_id: plans[0]?.pricing_id ?? 0 }));
      })
      .catch(() => {
        if (!cancelled) {
          setPlanOptions([]);
          setForm(prev => ({ ...prev, pricing_id: 0 }));
        }
      })
      .finally(() => {
        if (!cancelled) setPlansLoading(false);
      });
    return () => { cancelled = true; };
  }, [showCreate, form.account_type]);
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  // GET /admin/orders/?filter=&search=&pageNum=&pageSize=
  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(BaseUrl + 'admin/orders/', {
        params: { filter: statusFilter === 'all' ? 'all' : statusFilter, search, pageNum: page, pageSize: PAGE_SIZE },
      });
      const data = res.data;
      const rawResults: RawOrder[] = Array.isArray(data) ? data : (data?.results ?? []);
      const count = Array.isArray(data) ? rawResults.length : (data?.count ?? rawResults.length);
      setOrders(rawResults.map(mapOrder));
      setTotalCount(count);
      setTotalPages(Math.max(1, Math.ceil(count / PAGE_SIZE)));
    } catch {
      // const fallback = FALLBACK_ORDERS.filter(o =>
      //   (statusFilter === 'all' || o.status === statusFilter) &&
      //   (!search || o.customer_name.toLowerCase().includes(search.toLowerCase()) ||
      //     o.customer_email.toLowerCase().includes(search.toLowerCase()) ||
      //     o.order_code.toLowerCase().includes(search.toLowerCase()))
      // );
      // setOrders(fallback);
      // setTotalCount(fallback.length);
      // setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);


  const loadMetrics = useCallback(async () => {
    try {
      const res = await axios.get(BaseUrl + 'admin/orders/metrics/');
      setMetrics(res.data);
    } catch {
      // Leave whatever metrics we last had (or the zeroed default) rather
      // than silently showing wrong numbers derived from just this page.
    }
  }, []);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  const copyText = (text: string, label: string) => {
    navigator.clipboard?.writeText(text).then(() => showToast(`${label} copied to clipboard.`));
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await axios.post(BaseUrl + `admin/orders/${cancelTarget.id}/cancel/`, {});
      showToast(`Order ${cancelTarget.order_code} cancelled.`);
      await Promise.all([loadOrders(), loadMetrics()]);
    } catch {
      // Backend cancel call failed — reflect it locally so the panel stays
      // usable, but this won't be persisted server-side.
      setOrders(prev => prev.map(o => o.id === cancelTarget.id ? { ...o, status: 'cancelled' } : o));
      showToast(`Order ${cancelTarget.order_code} marked cancelled locally — server call failed.`, 'error');
    }
    setCancelling(false);
    setCancelTarget(null);
  };

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormError(null);
    setSelectedCustomer(null);
    setCustomerSearch('');
    setCustomerResults([]);
    setShowCreate(true);
  };

  const closeCreate = () => {
    if (creating) return;
    setShowCreate(false);
    setSelectedCustomer(null);
    setCustomerSearch('');
    setCustomerResults([]);
  };
  const updateForm = <K extends keyof NewOrderForm>(key: K, value: NewOrderForm[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const pickCustomer = (c: CustomerOption) => {
    setSelectedCustomer(c);
    updateForm('customer_name', c.full_name);
    updateForm('customer_email', c.email);
    setCustomerSearch('');
    setCustomerResults([]);
    setShowCustomerResults(false);
  };

  const clearCustomer = () => {
    setSelectedCustomer(null);
    updateForm('customer_name', '');
    updateForm('customer_email', '');
  };

  const submitCreate = async () => {
    setFormError(null);

    if (!form.customer_name.trim() || !form.customer_email.trim()) {
      setFormError('Customer name and email are required.');
      return;
    }
    if (!EMAIL_RE.test(form.customer_email.trim())) {
      setFormError('Enter a valid email address.');
      return;
    }
    if (!form.pricing_id || plansLoading) {
      setFormError('Select a plan — plans are still loading or none are available for this account type.');
      return;
    }
const amountNum = Number(form.amount);
if (form.account_type !== 'demo') {
  if (!form.amount || Number.isNaN(amountNum) || amountNum <= 0) {
    setFormError('Enter a valid amount greater than 0.');
    return;
  }
}
if (form.account_type !== 'demo' && form.status === 'paid' &&
    (!form.razorpay_payment_id.trim() || !form.razorpay_order_id.trim() || !form.razorpay_signature.trim())) {
  setFormError('Razorpay payment ID, order ID, and signature are required for a Paid order.');
  return;
}
    setCreating(true);
    const payload = {
      customer_name: form.customer_name.trim(),
      customer_email: form.customer_email.trim().toLowerCase(),
      account_type: form.account_type,
      pricing_id: form.pricing_id,
      amount: amountNum,
      payment_method: form.payment_method.trim() || 'razorpay',
      initial_status: form.status === 'paid' ? 'Paid' : 'Created',
      razorpay_payment_id: form.razorpay_payment_id.trim() || null,
      razorpay_order_id: form.razorpay_order_id.trim() || null,
      razorpay_signature: form.razorpay_signature.trim() || null,
    };

    try {
      const res = await axios.post(BaseUrl + 'admin/orders/create/', payload);
      const orderCode = res.data?.order_code ?? '';
      showToast(res.data?.message ? `${res.data.message}${orderCode ? ` (${orderCode})` : ''}` : `Order ${orderCode} created.`);
      setShowCreate(false);
      setPage(1);
      await Promise.all([loadOrders(), loadMetrics()]);
    } catch {
      const localOrder: OrderRow = {
        id: Date.now(),
        order_code: makeLocalOrderCode(),
        customer_name: payload.customer_name,
        customer_email: payload.customer_email,
        plan_name: planOptions.find(p => p.pricing_id === payload.pricing_id)?.name ?? '—',
        billing_cycle: planOptions.find(p => p.pricing_id === payload.pricing_id)?.billing_cycle ?? '—',
        payment_method: payload.payment_method,
        amount: amountNum,
        currency: 'INR',
        status: form.status,
        created_at: new Date().toISOString(),
      };
      // setOrders(prev => [localOrder, ...prev]);
      setTotalCount(c => c + 1);
      showToast(`Order ${localOrder.order_code} saved locally — server call failed, nothing was actually created.`, 'error');
      setShowCreate(false);
    } finally {
      setCreating(false);
    }
  };

  const columns: Column<OrderRow>[] = [
    {
      key: 'order_code', header: 'Order ID', render: r => (
        <div className="flex items-center gap-2">
          <span className="font-mono text-[11.5px] font-bold text-gray-800">{r.order_code}</span>
          <button
            onClick={e => { e.stopPropagation(); copyText(r.order_code, 'Order ID'); }}
            title="Copy order ID"
            className="text-gray-300 hover:text-navy transition-colors"
          >
            <FontAwesomeIcon icon={faCopy} className="text-[11px]" />
          </button>
        </div>
      )
    },
    {
      key: 'customer', header: 'Customer', render: r => (
        <div>
          <p className="font-bold text-gray-900">{r.customer_name}</p>
          <p className="text-[11px] text-gray-400 font-medium">{r.customer_email}</p>
        </div>
      )
    },
    { key: 'plan', header: 'Plan', render: r => <>{r.plan_name} <span className="text-gray-400">({r.billing_cycle})</span></>, hideBelow: 'md' },
    { key: 'method', header: 'Method', render: r => r.payment_method || <span className="text-gray-300">—</span>, hideBelow: 'lg' },
    { key: 'amount', header: 'Amount', render: r => formatAmount(r), align: 'right' },
    { key: 'status', header: 'Status', render: r => <Badge tone={STATUS_TONE[r.status]}>{r.status}</Badge> },
    { key: 'created_at', header: 'Created', render: r => formatDateTime(r.created_at), hideBelow: 'md' },
    {
      key: 'actions', header: 'Action', align: 'right', render: r => (
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={e => { e.stopPropagation(); setSelected(r); }}
            title="View details"
            className="w-7 h-7 flex items-center justify-center rounded-lg text-navy hover:text-navy hover:bg-gray-50 transition-colors"
          >
            <FontAwesomeIcon icon={faEye} className="text-[12px]" />
          </button>
          {(r.status === 'created' || r.status === 'attempted') && (
            <button
              onClick={e => { e.stopPropagation(); setCancelTarget(r); }}
              title="Cancel order"
              className="w-7 h-7 flex items-center justify-center rounded-md text-red-600 hover:text-red-600 hover:bg-red-50 transition-colors"
            >
              <FontAwesomeIcon icon={faBan} className="text-[12px]" />
            </button>
          )}
        </div>
      )
    },
  ];

  return (
    <div>
      <PageHeader
        title="Order Management"
        subtitle="Every checkout order created on Manna Plag, from initiation through payment."
        action={
          <Button variant="primary" size="sm" icon="ti-plus" onClick={openCreate}>
            <FontAwesomeIcon icon={faPlus} className="text-[11px] mr-1.5" />
            New Order
          </Button>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Orders" value={metrics.total_orders} icon={faShoppingCart} iconBg="#eff6ff" iconColor="#2563eb" />
        <StatCard label="Paid" value={metrics.paid} icon={faCircleCheck} iconBg="#f0fdf4" iconColor="#16a34a" />
        <StatCard label="Created / Attempted" value={metrics.created_attempted} icon={faHourglassHalf} iconBg="#fffbeb" iconColor="#d97706" />
        <StatCard label="Failed / Cancelled" value={metrics.failed_cancelled} icon={faCircleXmark} iconBg="#fef2f2" iconColor="#dc2626" />
      </div>

      <Card>
        <CardHeader
          title="Orders"
          subtitle="All checkout orders, newest first"
          action={<SearchInput value={search} onChange={setSearch} placeholder="Search order code, customer, email…" className="w-72" />}
        />
        <div className="flex gap-1.5 flex-wrap mb-4 -mt-1">
          {STATUS_TABS.map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`text-[11px] font-bold px-2.5 py-1.5 rounded-lg capitalize transition-colors ${statusFilter === s ? 'bg-navy text-white' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                }`}
            >
              {s}
            </button>
          ))}
        </div>
        <DataTable columns={columns} rows={orders} loading={loading} onRowClick={setSelected} emptyTitle="No orders found" emptySubtitle="Try a different filter or search term." />
        <Pagination page={page} totalPages={totalPages} onChange={setPage} totalItems={totalCount} pageSize={PAGE_SIZE} />
      </Card>

      {/* Order details modal */}
      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        title="Order details"
        footer={selected && (
          <>
            <Button variant="secondary" size="sm" onClick={() => setSelected(null)}>Close</Button>
            {/* {(selected.status === 'created' || selected.status === 'attempted') && (
              <Button variant="danger" size="sm" icon="ti-ban" onClick={() => { setCancelTarget(selected); setSelected(null); }}>
                Cancel order
              </Button>
            )} */}
          </>
        )}
      >
        {selected && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-navy text-white flex items-center justify-center font-black text-[14px] flex-shrink-0">
                {selected.customer_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <div>
                <p className="font-black text-gray-900 text-[14px]">{selected.customer_name}</p>
                <p className="text-[12px] text-gray-500">{selected.customer_email}</p>
              </div>
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 flex items-center justify-between">
              <span className="text-[11px] font-bold text-gray-500 font-mono">{selected.order_code}</span>
              <button onClick={() => copyText(selected.order_code, 'Order ID')} className="text-gray-400 hover:text-navy">
                <FontAwesomeIcon icon={faCopy} className="text-[12px]" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-1">
              {[
                { label: 'Plan', value: `${selected.plan_name} ${selected.billing_cycle}` },
                { label: 'Status', value: <Badge tone={STATUS_TONE[selected.status]}>{selected.status}</Badge> },
                { label: 'Payment method', value: selected.payment_method || '—' },
                { label: 'Amount', value: formatAmount(selected) },
                { label: 'Created', value: formatDateTime(selected.created_at) },
              ].map(f => (
                <div key={f.label}>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">{f.label}</p>
                  <div className="text-[13px] font-semibold text-gray-800">{f.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>

      {/* Create order modal */}
      <Modal
        open={showCreate}
        onClose={closeCreate}
        title="New order"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={closeCreate} disabled={creating}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={submitCreate} loading={creating}>
              Create order
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {formError && (
            <div className="bg-red-50 border border-red-100 text-red-600 text-[12px] font-semibold rounded-lg px-3 py-2">
              {formError}
            </div>
          )}
          {/* Search existing customer */}
          <div className="relative">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1 block">
              Search existing customer <span className="normal-case font-medium">(optional — leave blank for a new customer)</span>
            </label>

            {selectedCustomer ? (
              <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <div>
                  <p className="text-[13px] font-bold text-gray-800">{selectedCustomer.full_name}</p>
                  <p className="text-[11px] text-gray-400">{selectedCustomer.email}</p>
                </div>
                <button
                  type="button"
                  onClick={clearCustomer}
                  className="text-[11px] font-bold text-gray-400 hover:text-red-600"
                >
                  Change
                </button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  value={customerSearch}
                  onChange={e => setCustomerSearch(e.target.value)}
                  onFocus={() => customerResults.length > 0 && setShowCustomerResults(true)}
                  onBlur={() => setTimeout(() => setShowCustomerResults(false), 150)}
                  placeholder="Search by name or email…"
                  className="w-full text-[13px] font-semibold text-gray-800 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy"
                />
                {showCustomerResults && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                    {customerSearchLoading && (
                      <div className="px-3 py-2 text-[12px] text-gray-400">Searching…</div>
                    )}
                    {!customerSearchLoading && customerResults.length === 0 && (
                      <div className="px-3 py-2 text-[12px] text-gray-400">No matching customers</div>
                    )}
                    {!customerSearchLoading && customerResults.map(c => (
                      <button
                        key={c.id}
                        type="button"
                        onMouseDown={() => pickCustomer(c)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-50 last:border-b-0"
                      >
                        <p className="text-[12.5px] font-bold text-gray-800">{c.full_name}</p>
                        <p className="text-[11px] text-gray-400">{c.email}{c.organisation ? ` · ${c.organisation}` : ''}</p>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 sm:col-span-1">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1 block">Customer name</label>
              <input
                type="text"
                value={form.customer_name}
                onChange={e => updateForm('customer_name', e.target.value)}
                readOnly={!!selectedCustomer}
                placeholder="enter customer name"
                className="w-full text-[13px] font-semibold text-gray-800 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy"
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1 block">Customer email</label>
              <input
                type="email"
                value={form.customer_email}
                onChange={e => updateForm('customer_email', e.target.value)}
                placeholder="enter email"
                className="w-full text-[13px] font-semibold text-gray-800 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy"
              />
            </div>

            <div>
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1 block">Account type</label>
              <select
                value={form.account_type}
                onChange={e => updateForm('account_type', e.target.value as NewOrderForm['account_type'])}
                className="w-full text-[13px] font-semibold text-gray-800 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy"
              >
                {ACCOUNT_TYPE_OPTIONS.map(a => (
                  <option key={a} value={a}>{ACCOUNT_TYPE_LABELS[a]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1 block">Plan</label>
              <select
                value={form.pricing_id}
                disabled={plansLoading || planOptions.length === 0}
                onChange={e => {
                  const pricingId = Number(e.target.value);
                  const plan = planOptions.find(p => p.pricing_id === pricingId);
                  setForm(prev => ({
                    ...prev,
                    pricing_id: pricingId,
                    // Pre-fill the amount from the selected plan's price so
                    // it stays in sync with what's actually being charged;
                    // the admin can still override it below if needed.
                    amount: plan ? String(plan.total_amount ?? plan.price) : prev.amount,
                  }));
                }}
                className="w-full text-[13px] font-semibold text-gray-800 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy disabled:bg-gray-50 disabled:text-gray-400"
              >
                {plansLoading && <option>Loading plans…</option>}
                {!plansLoading && planOptions.length === 0 && <option>No plans available</option>}
                {!plansLoading && planOptions.map(p => (
                  <option key={p.pricing_id} value={p.pricing_id}>
                    {p.name} ({p.billing_cycle}) {/* — {p.currency_symbol}{Number(p.total_amount ?? p.price).toLocaleString('en-IN')} */}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1 block">Amount (₹)</label>
              <input
                type="number"
                min={0}
                value={form.amount}
                onChange={e => updateForm('amount', e.target.value)}
                placeholder="enter amount"
                className="w-full text-[13px] font-semibold text-gray-800 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1 block">Payment method</label>
              <select
                value={form.payment_method}
                onChange={e => updateForm('payment_method', e.target.value as NewOrderForm['payment_method'])}
                className="w-full text-[13px] font-semibold text-gray-800 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy"
              >
                {PAYMENT_METHOD_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>

            <div className="col-span-2">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1 block">Initial status</label>
              <div className="flex gap-1.5">
                {CREATE_STATUS_OPTIONS.map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => updateForm('status', s)}
                    className={`text-[11px] font-bold px-3 py-1.5 rounded-lg capitalize transition-colors ${form.status === s ? 'bg-navy text-white' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                      }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {form.status === 'paid' && (
              <>
                <div className="col-span-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1 block">
                    Transaction ID
                  </label>
                  <input
                    type="text"
                    value={form.razorpay_payment_id}
                    onChange={e => updateForm('razorpay_payment_id', e.target.value)}
                    placeholder="UPI ref/bank TXN/Razorpay ID"
                    className="w-full text-[13px] font-mono font-semibold text-gray-800 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1 block">
                    Razorpay order ID
                  </label>
                  <input
                    type="text"
                    value={form.razorpay_order_id}
                    onChange={e => updateForm('razorpay_order_id', e.target.value)}
                    placeholder="order_QZk8xxxxxxxxxx"
                    className="w-full text-[13px] font-mono font-semibold text-gray-800 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1 block">
                    Razorpay signature
                  </label>
                  <input
                    type="text"
                    value={form.razorpay_signature}
                    onChange={e => updateForm('razorpay_signature', e.target.value)}
                    placeholder="5f4dcc3b5aa765d61d8327deb882cf99"
                    className="w-full text-[13px] font-mono font-semibold text-gray-800 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!cancelTarget}
        onClose={() => !cancelling && setCancelTarget(null)}
        onConfirm={confirmCancel}
        title="Cancel order"
        message={cancelTarget ? `Cancel order ${cancelTarget.order_code} for ${cancelTarget.customer_name}? This can't be undone.` : ''}
        confirmLabel="Cancel order"
        danger
        loading={cancelling}
      />

      <Toast message={toast} />
    </div>
  );
}